import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';
import { Button, Notice } from './ui.js';

type Format = 'csv' | 'md';

/** The name the API gives the file (`<brand>-<host>-<day>.<ext>`), else a plain one. */
function fileNameOf(response: Response | undefined, format: Format): string {
  const match = /filename="([^"]+)"/.exec(response?.headers.get('content-disposition') ?? '');
  return match?.[1] ?? `export-${new Date().toISOString().slice(0, 10)}.${format}`;
}

export async function fetchExport(
  siteId: string,
  format: Format,
  locale: 'de' | 'en' | 'uk',
  /** Markdown only: just the critical and high problems (T-99). */
  scope: 'all' | 'urgent' = 'all',
) {
  const params = { path: { siteId }, query: { locale } };
  const { data, error, response } =
    format === 'csv'
      ? await api.GET('/sites/{siteId}/findings.csv', { params, parseAs: 'blob' })
      : await api.GET('/sites/{siteId}/findings.md', {
          params: { ...params, query: { locale, scope } },
          parseAs: 'blob',
        });
  if (error || !(data instanceof Blob)) throw error ?? new Error('download');
  return { blob: data, name: fileNameOf(response, format) };
}

export function save(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Saves the open problems of a site as a file: CSV for Excel (T-43), Markdown (T-54a). */
export function ExportButton({ siteId, format }: { siteId: string; format: Format }) {
  const { t, i18n } = useTranslation();
  const download = useMutation({
    mutationFn: async () => {
      const { blob, name } = await fetchExport(siteId, format, toLanguage(i18n.language));
      save(blob, name);
    },
  });
  return (
    <>
      <Button variant="secondary" busy={download.isPending} onClick={() => download.mutate()}>
        {t(format === 'csv' ? 'report.csv' : 'report.md')}
      </Button>
      {download.isError ? <Notice kind="error">{errorMessage(download.error, t)}</Notice> : null}
    </>
  );
}

/**
 * The Markdown task list for a developer or an AI assistant (T-54a): as a file, or copied to paste
 * straight into a chat.
 */
export function MarkdownExport({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = useMutation({
    mutationFn: async () => {
      const { blob } = await fetchExport(siteId, 'md', toLanguage(i18n.language));
      await navigator.clipboard.writeText(await blob.text());
    },
    onSuccess: () => setCopied(true),
  });
  return (
    <>
      <ExportButton siteId={siteId} format="md" />
      <Button variant="secondary" busy={copy.isPending} onClick={() => copy.mutate()}>
        {t('report.mdCopy')}
      </Button>
      {copied ? (
        <p role="status" className="text-sm text-slate-700">
          {t('report.mdCopied')}
        </p>
      ) : null}
      {copy.isError ? <Notice kind="error">{errorMessage(copy.error, t)}</Notice> : null}
    </>
  );
}
